/**
 * A calendar day as the date inputs hold it and as the URL carries it:
 * `YYYY-MM-DD`, without a time and without a zone.
 */
export const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The affiliate's timezone. An appointment or hand-in time is a moment agreed
 * in the Netherlands, so it must read the same on a laptop travelling abroad.
 */
export const TIME_ZONE = "Europe/Amsterdam";

const timeZoneDayFormatter = new Intl.DateTimeFormat("nl-NL", {
  timeZone: TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Today as a `YYYY-MM-DD` day in {@link TIME_ZONE}, independent of the
 * browser's timezone. Unlike `toISODate(new Date())`, which uses the local day.
 */
export function todayInTimeZone(): string {
  const parts = timeZoneDayFormatter.formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Zero-pads to two digits. */
function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/** Whether a value is a `YYYY-MM-DD` day. */
export function isDay(value: string): boolean {
  return DAY_PATTERN.test(value);
}

/**
 * Serializes a `Date`'s local calendar day to `YYYY-MM-DD`. Deliberately not
 * `toISOString()`, which converts to UTC and can hand back yesterday for
 * evening timezones.
 */
export function toISODate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Parses a `YYYY-MM-DD` string into a local `Date`. Returns `null` for empty
 * or malformed input, so callers can treat "no valid selection" uniformly.
 * Constructed at local noon rather than midnight so DST shifts can never bump
 * the instant across a day boundary.
 */
export function parseISODate(value: string): Date | null {
  const match = DAY_PATTERN.exec(value);
  if (!match) return null;

  const [, year, month, date] = match;
  const y = Number(year);
  const m = Number(month);
  const d = Number(date);
  const parsed = new Date(y, m - 1, d, 12);

  // Reject overflow dates: `new Date` normalizes impossible days (e.g.
  // 2024-02-31 -> Mar 2), so verify the parts round-trip before trusting it.
  if (
    parsed.getFullYear() !== y ||
    parsed.getMonth() !== m - 1 ||
    parsed.getDate() !== d
  ) {
    return null;
  }

  return parsed;
}

/**
 * The day an instant from the API falls on, as a date input holds it. An
 * empty or unparseable value reads as "no day" rather than as a broken field,
 * so a caller never has to tell the two apart.
 */
export function toDay(value: string | null | undefined): string {
  if (!value) return "";

  const known = new Date(value);

  return Number.isNaN(known.getTime()) ? "" : toISODate(known);
}

/**
 * Turns a chosen day into the instant the API stores. Local noon rather than
 * midnight, so a DST shift can never move the day to the one before.
 *
 * Returns `null` for anything that is not a day, so "nothing chosen" and
 * "cleared" arrive at the API as the same absent value.
 */
export function dayToInstant(day: string): string | null {
  return parseISODate(day)?.toISOString() ?? null;
}

/**
 * The calendar day `days` before `date`. Goes through the local calendar
 * (`setDate` on the day number), not millisecond subtraction: subtracting
 * `days * 24 * 3600 * 1000` lands on the wrong day when a daylight-saving
 * transition makes one of the crossed days 23 or 25 hours long.
 */
export function subCalendarDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

/**
 * The local midnight the instant falls after, with its time dropped.
 */
export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** The last local second of the day the instant falls on. */
export function endOfDay(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
  );
}

/** Whether two instants fall on the same local calendar day. */
export function isSameDay(date: Date, other: Date): boolean {
  return (
    date.getFullYear() === other.getFullYear() &&
    date.getMonth() === other.getMonth() &&
    date.getDate() === other.getDate()
  );
}

/**
 * Whether the first instant falls on an earlier day than the second. Compares
 * days rather than moments, so something due this morning is not already past
 * for the rest of the day.
 */
export function isBeforeDay(date: Date, other: Date): boolean {
  return startOfDay(date).getTime() < startOfDay(other).getTime();
}

/**
 * Monday-based weekday index (0 = Monday … 6 = Sunday) for a date, matching
 * the weekday labels in `@/lib/format`. `getDay()` is Sunday-based, so we
 * rotate it.
 */
export function mondayIndex(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/**
 * Builds the 6×7 grid of days for a month view: the days of `month`, padded
 * at the front with trailing days of the previous month so the 1st lands on
 * its real weekday, and at the back to a full 42 cells (6 weeks) so the grid
 * height never jumps between months.
 */
export function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const leading = mondayIndex(first);
  const start = new Date(year, month, 1 - leading);

  return Array.from({ length: 42 }, (_, index) => {
    return new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + index,
    );
  });
}
